import { z } from "zod"

/**
 * CRM заявок продавцов (04.10.2026).
 *
 * Воронка подключения (`seller_applications`, стадии new → … → active) уже
 * была: доска, сроки SLA, ответственные, волны. Здесь — то, чего ей не хватало,
 * чтобы менеджер вёл бизнес от первого звонка до запуска в одном месте:
 * карточка заявки с данными компании, контактами, лентой касаний, задачами
 * «следующий шаг» и файлами (прайсы, каталоги, фото, договоры).
 *
 * Стадии и их правила остаются в шлюзе (`apps/api-gateway/src/types/application.ts`):
 * этот модуль их не переопределяет, а только добавляет данные вокруг заявки.
 * Поэтому `application` в карточке описан минимальной формой — полную
 * (`SellerApplication`) админка берёт из своих типов.
 */

// ── Справочники ──────────────────────────────────────────────────────────

/** Откуда пришёл бизнес. `landing` ставит сам шлюз для заявок с лендинга. */
export const CRM_LEAD_SOURCES = [
  "landing",
  "manual",
  "call",
  "exhibition",
  "referral",
  "cold_base",
  "marketplace_scout",
  "social",
  "ai_import",
  "other",
] as const
export const CrmLeadSourceSchema = z.enum(CRM_LEAD_SOURCES)
export type CrmLeadSource = z.infer<typeof CrmLeadSourceSchema>

export const CRM_LEAD_SOURCE_LABELS: Record<CrmLeadSource, string> = {
  landing: "Лендинг",
  manual: "Заведена вручную",
  call: "Входящий звонок",
  exhibition: "Выставка, мероприятие",
  referral: "Рекомендация",
  cold_base: "Холодная база",
  marketplace_scout: "Нашли на маркетплейсе",
  social: "Соцсети",
  ai_import: "ИИ-загрузка каталога",
  other: "Другое",
}

/** Касание в ленте заявки. */
export const CRM_ACTIVITY_KINDS = ["comment", "call", "meeting", "email", "message"] as const
export const CrmActivityKindSchema = z.enum(CRM_ACTIVITY_KINDS)
export type CrmActivityKind = z.infer<typeof CrmActivityKindSchema>

export const CRM_ACTIVITY_KIND_LABELS: Record<CrmActivityKind, string> = {
  comment: "Комментарий",
  call: "Звонок",
  meeting: "Встреча",
  email: "Письмо",
  message: "Сообщение",
}

/** Итог звонка или встречи — по нему видно, сколько дозвонов впустую. */
export const CRM_ACTIVITY_OUTCOMES = [
  "reached",
  "no_answer",
  "callback",
  "interested",
  "thinking",
  "not_interested",
] as const
export const CrmActivityOutcomeSchema = z.enum(CRM_ACTIVITY_OUTCOMES)
export type CrmActivityOutcome = z.infer<typeof CrmActivityOutcomeSchema>

export const CRM_ACTIVITY_OUTCOME_LABELS: Record<CrmActivityOutcome, string> = {
  reached: "Поговорили",
  no_answer: "Не дозвонились",
  callback: "Просили перезвонить",
  interested: "Интересно",
  thinking: "Думают",
  not_interested: "Неинтересно",
}

/** Вид задачи «следующий шаг». */
export const CRM_TASK_KINDS = [
  "call",
  "meeting",
  "email",
  "message",
  "send_offer",
  "collect_materials",
  "upload_catalog",
  "other",
] as const
export const CrmTaskKindSchema = z.enum(CRM_TASK_KINDS)
export type CrmTaskKind = z.infer<typeof CrmTaskKindSchema>

export const CRM_TASK_KIND_LABELS: Record<CrmTaskKind, string> = {
  call: "Позвонить",
  meeting: "Встреча",
  email: "Написать письмо",
  message: "Написать в мессенджер",
  send_offer: "Отправить предложение",
  collect_materials: "Запросить материалы",
  upload_catalog: "Загрузить каталог",
  other: "Другое",
}

/** Раздел файла в карточке. */
export const CRM_FILE_CATEGORIES = [
  "price_list",
  "catalog",
  "photos",
  "documents",
  "contract",
  "presentation",
  "other",
] as const
export const CrmFileCategorySchema = z.enum(CRM_FILE_CATEGORIES)
export type CrmFileCategory = z.infer<typeof CrmFileCategorySchema>

export const CRM_FILE_CATEGORY_LABELS: Record<CrmFileCategory, string> = {
  price_list: "Прайс, остатки",
  catalog: "Каталог",
  photos: "Фото товаров",
  documents: "Документы компании",
  contract: "Договор",
  presentation: "Презентация",
  other: "Другое",
}

/** Предел одного файла заявки, байт. */
export const CRM_FILE_MAX_BYTES = 50 * 1024 * 1024

// ── Сущности ─────────────────────────────────────────────────────────────

/** Сотрудник площадки в карточке: ответственный, автор касания. */
export type CrmPerson = { id: string; name: string; initials: string }

export type CrmContact = {
  id: string
  name: string
  /** Должность или роль: «собственник», «менеджер по маркетплейсам». */
  role: string
  phone: string
  email: string
  telegram: string
  isPrimary: boolean
  createdAt: string
}

export const CrmContactInputSchema = z.object({
  name: z.string().trim().min(1).max(160),
  role: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(40).optional(),
  email: z.string().trim().max(160).optional(),
  telegram: z.string().trim().max(80).optional(),
  isPrimary: z.boolean().optional(),
})
export type CrmContactInput = z.infer<typeof CrmContactInputSchema>
export const CrmContactPatchSchema = CrmContactInputSchema.partial()
export type CrmContactPatch = z.infer<typeof CrmContactPatchSchema>

/** Данные компании. Заполняются из DaData по ИНН или руками. */
export const CrmCompanySchema = z.object({
  inn: z.string().trim().max(12).optional(),
  kpp: z.string().trim().max(9).optional(),
  ogrn: z.string().trim().max(15).optional(),
  shortName: z.string().trim().max(300).optional(),
  fullName: z.string().trim().max(500).optional(),
  /** ООО, ИП, АО, самозанятый. */
  orgForm: z.string().trim().max(60).optional(),
  address: z.string().trim().max(500).optional(),
  director: z.string().trim().max(200).optional(),
  /** Действующая / ликвидирована — как отдаёт DaData. */
  state: z.string().trim().max(40).optional(),
  registeredAt: z.string().trim().max(40).optional(),
  /** Основной ОКВЭД с расшифровкой. */
  okved: z.string().trim().max(300).optional(),
  filledFrom: z.enum(["dadata", "manual"]).optional(),
  filledAt: z.string().optional(),
})
export type CrmCompany = z.infer<typeof CrmCompanySchema>

/** Где бизнес уже есть: сайт, соцсети, маркетплейсы. Пустая строка — нет. */
export const CrmLinksSchema = z.object({
  website: z.string().trim().max(500).optional(),
  instagram: z.string().trim().max(500).optional(),
  vk: z.string().trim().max(500).optional(),
  telegram: z.string().trim().max(500).optional(),
  wildberries: z.string().trim().max(500).optional(),
  ozon: z.string().trim().max(500).optional(),
  yandexMarket: z.string().trim().max(500).optional(),
  avito: z.string().trim().max(500).optional(),
  other: z.string().trim().max(1000).optional(),
})
export type CrmLinks = z.infer<typeof CrmLinksSchema>

/** CRM-поля заявки поверх данных воронки. */
export type CrmLeadProfile = {
  source: CrmLeadSource
  /** Уточнение источника: название выставки, кто порекомендовал. */
  sourceDetail: string
  company: CrmCompany
  links: CrmLinks
  tags: string[]
  /** Оценка ассортимента, SKU. */
  assortmentSize: number | null
  /** Средний чек, руб. */
  avgCheck: number | null
  /** Оценка оборота на площадке, руб. в месяц. */
  monthlyPotential: number | null
  /** Свободная заметка менеджера (seller_applications.note). */
  comment: string
}

/**
 * Правка заявки менеджером. Основные поля воронки (название, контакт,
 * телефон…) правятся здесь же: раньше их нельзя было поменять вовсе.
 */
export const CrmLeadPatchSchema = z.object({
  shopName: z.string().trim().min(1).max(160).optional(),
  contactName: z.string().trim().min(1).max(120).optional(),
  phone: z.string().trim().max(30).optional(),
  email: z.string().trim().max(160).optional(),
  city: z.string().trim().max(120).optional(),
  inn: z.string().trim().max(20).optional(),
  categories: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
  source: CrmLeadSourceSchema.optional(),
  sourceDetail: z.string().trim().max(300).optional(),
  company: CrmCompanySchema.optional(),
  links: CrmLinksSchema.optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  assortmentSize: z.number().int().nonnegative().max(1_000_000).nullable().optional(),
  avgCheck: z.number().nonnegative().max(100_000_000).nullable().optional(),
  monthlyPotential: z.number().nonnegative().max(10_000_000_000).nullable().optional(),
  comment: z.string().max(5000).optional(),
})
export type CrmLeadPatch = z.infer<typeof CrmLeadPatchSchema>

/** Заявка, заведённая менеджером (звонок, выставка, холодная база). */
export const CrmLeadCreateSchema = z.object({
  shopName: z.string().trim().min(1).max(160),
  contactName: z.string().trim().min(1).max(120),
  phone: z.string().trim().min(5).max(30),
  email: z.string().trim().max(160).optional(),
  city: z.string().trim().max(120).optional(),
  inn: z.string().trim().max(20).optional(),
  categories: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
  source: CrmLeadSourceSchema.default("manual"),
  sourceDetail: z.string().trim().max(300).optional(),
  comment: z.string().max(5000).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  links: CrmLinksSchema.optional(),
  /** Пусто — ответственным становится тот, кто завёл. null — без ответственного. */
  assigneeId: z.string().nullable().optional(),
  /** Завести, даже если нашлись дубли по телефону/почте/ИНН. */
  allowDuplicate: z.boolean().optional(),
})
export type CrmLeadCreate = z.infer<typeof CrmLeadCreateSchema>

export type CrmTask = {
  id: string
  applicationId: string
  title: string
  kind: CrmTaskKind
  /** Срок — момент времени (ISO). null — без срока. */
  dueAt: string | null
  assignee: CrmPerson | null
  doneAt: string | null
  doneBy: CrmPerson | null
  createdAt: string
  createdBy: CrmPerson | null
  /** Открыта и срок прошёл (считает шлюз по московскому времени). */
  overdue: boolean
}

export const CrmTaskInputSchema = z.object({
  title: z.string().trim().min(1).max(300),
  kind: CrmTaskKindSchema.default("call"),
  dueAt: z.string().datetime({ offset: true }).nullable().optional(),
  /** Пусто — ответственный заявки, иначе автор. */
  assigneeId: z.string().nullable().optional(),
})
export type CrmTaskInput = z.infer<typeof CrmTaskInputSchema>

export const CrmTaskPatchSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  kind: CrmTaskKindSchema.optional(),
  dueAt: z.string().datetime({ offset: true }).nullable().optional(),
  assigneeId: z.string().nullable().optional(),
  done: z.boolean().optional(),
})
export type CrmTaskPatch = z.infer<typeof CrmTaskPatchSchema>

/** Задача со ссылкой на заявку — для списка «Мои задачи». */
export type CrmTaskWithLead = CrmTask & {
  application: { id: string; shopName: string; contactName: string; status: string; phone: string }
}

export const CRM_TASK_SCOPES = ["mine", "all"] as const
export const CRM_TASK_DUE_FILTERS = ["overdue", "today", "week", "open", "done"] as const
export type CrmTaskScope = (typeof CRM_TASK_SCOPES)[number]
export type CrmTaskDueFilter = (typeof CRM_TASK_DUE_FILTERS)[number]

export type CrmFile = {
  id: string
  applicationId: string
  filename: string
  mime: string
  sizeBytes: number
  category: CrmFileCategory
  /** Картинка — консоль показывает превью. */
  isImage: boolean
  uploadedBy: CrmPerson | null
  createdAt: string
  /** Путь шлюза для скачивания (с токеном консоли): `/admin/applications/:id/files/:fileId`. */
  downloadPath: string
}

export const CrmFilePatchSchema = z.object({
  category: CrmFileCategorySchema.optional(),
  filename: z.string().trim().min(1).max(255).optional(),
})
export type CrmFilePatch = z.infer<typeof CrmFilePatchSchema>

export type CrmActivity = {
  id: string
  kind: CrmActivityKind
  body: string
  outcome: CrmActivityOutcome | null
  /** Когда было касание (звонок мог быть вчера, а записали сегодня). */
  occurredAt: string
  author: CrmPerson | null
  createdAt: string
  editedAt: string | null
  /** Файлы заявки, приложенные к касанию. */
  fileIds: string[]
}

export const CrmActivityInputSchema = z.object({
  kind: CrmActivityKindSchema,
  body: z.string().trim().max(10000).default(""),
  outcome: CrmActivityOutcomeSchema.nullable().optional(),
  occurredAt: z.string().datetime({ offset: true }).optional(),
  fileIds: z.array(z.string()).max(20).optional(),
  /** Закрыть открытую задачу этим касанием («позвонил — отметил»). */
  completeTaskId: z.string().optional(),
  /** И сразу поставить следующий шаг. */
  nextTask: CrmTaskInputSchema.optional(),
})
export type CrmActivityInput = z.infer<typeof CrmActivityInputSchema>

export const CrmActivityPatchSchema = z.object({
  body: z.string().trim().max(10000).optional(),
  outcome: CrmActivityOutcomeSchema.nullable().optional(),
  occurredAt: z.string().datetime({ offset: true }).optional(),
})
export type CrmActivityPatch = z.infer<typeof CrmActivityPatchSchema>

/** Лента карточки — всё, что происходило с заявкой, одним списком (новые сверху). */
export type CrmTimelineItem =
  | { type: "activity"; at: string; activity: CrmActivity }
  | {
      type: "status"
      at: string
      fromStatus: string | null
      toStatus: string
      message: string
      automated: boolean
      actor: CrmPerson | null
    }
  | { type: "task"; at: string; action: "created" | "done"; task: CrmTask }
  | { type: "file"; at: string; file: CrmFile }

export type CrmDuplicate = {
  id: string
  shopName: string
  contactName: string
  status: string
  submittedAt: string
  matchedBy: ("phone" | "email" | "inn")[]
}

/** Кабинет продавца, если он уже заведён по заявке. */
export type CrmLeadAccount = {
  userId: string
  email: string
  /** Магазин продавца (до публикации — id самого продавца). */
  shopId: string
  shopName: string
  onboardingStatus: string
  productsCount: number
  publishedProductsCount: number
}

/** ИИ-загрузки каталога, запущенные по этой заявке. */
export type CrmLeadImportJob = {
  id: string
  title: string
  status: string
  createdAt: string
  itemsTotal: number
  itemsCommitted: number
}

/**
 * Полная карточка заявки. `application` — та же форма, что строка доски
 * (`SellerApplication` шлюза: стадия, SLA, ответственный, история смен стадий).
 */
export type CrmLeadCard<TApplication = CrmApplicationHead> = {
  application: TApplication
  profile: CrmLeadProfile
  contacts: CrmContact[]
  /** Открытые сначала (по сроку), затем закрытые (новые сверху). */
  tasks: CrmTask[]
  /** Ближайшая открытая задача — «следующий шаг». */
  nextTask: CrmTask | null
  files: CrmFile[]
  timeline: CrmTimelineItem[]
  duplicates: CrmDuplicate[]
  account: CrmLeadAccount | null
  importJobs: CrmLeadImportJob[]
}

/** Минимальная форма заявки в контракте; полная — `SellerApplication` шлюза. */
export type CrmApplicationHead = {
  id: string
  shopName: string
  contactName: string
  city: string
  categories: string[]
  status: string
  phone?: string
  email?: string
  inn?: string
  userId?: string
  submittedAt: string
  statusChangedAt: string
}

/**
 * Что CRM добавляет к строке доски заявок (поля необязательные: старая
 * доска и мок без них продолжают работать).
 */
export type CrmBoardExtras = {
  source?: CrmLeadSource
  tags?: string[]
  nextTask?: { id: string; title: string; kind: CrmTaskKind; dueAt: string | null; overdue: boolean } | null
  openTasks?: number
  filesCount?: number
  /** Последнее касание (не смена стадии) — для «давно не связывались». */
  lastActivityAt?: string | null
}

// ── Загрузка базы заявок из таблицы ─────────────────────────────────────

export const CRM_IMPORT_FIELDS = [
  "shopName",
  "contactName",
  "phone",
  "email",
  "city",
  "inn",
  "categories",
  "website",
  "instagram",
  "vk",
  "telegram",
  "wildberries",
  "ozon",
  "comment",
  "tags",
] as const
export const CrmImportFieldSchema = z.enum(CRM_IMPORT_FIELDS)
export type CrmImportField = z.infer<typeof CrmImportFieldSchema>

export const CrmLeadImportSchema = z.object({
  headers: z.array(z.string().max(300)).max(100),
  rows: z.array(z.array(z.string().max(5000)).max(100)).min(1).max(5000),
  /** Колонка → поле; null — не загружать. */
  mapping: z.array(CrmImportFieldSchema.nullable()).max(100),
  source: CrmLeadSourceSchema.default("cold_base"),
  sourceDetail: z.string().trim().max(300).optional(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
  assigneeId: z.string().nullable().optional(),
  /** true — только проверить и показать, что будет. */
  dryRun: z.boolean().default(true),
})
export type CrmLeadImport = z.infer<typeof CrmLeadImportSchema>

export type CrmLeadImportResult = {
  dryRun: boolean
  total: number
  created: number
  /** Дубли существующих заявок (по телефону/почте/ИНН) — не заводятся. */
  duplicates: number
  invalid: number
  rows: {
    row: number
    status: "create" | "created" | "duplicate" | "invalid"
    shopName: string
    message?: string
    applicationId?: string
  }[]
}
